import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import StartSubpageLayout from '../components/start/StartSubpageLayout';
import { startCard, startCardPadding, startPrimaryButton } from '../components/start/startPageTokens';

/**
 * Module scope so other surfaces render the same roster without duplicating
 * bios — the welcome popup panels import this.
 */
export const BBA_TEAM_MEMBERS = [
  {
    id: 1,
    name: 'KEN COMBS',
    title: 'Scaling & Sales',
    bio: 'Ken Combs is the visionary founder and CEO of Builders Breakthrough Advisors, bringing decades of experience in the construction industry.',
    avatarUrl: '/kenshot.png',
    department: 'SALES',
    background: 'Ken\'s journey is nothing short of remarkable, rooted in a deep passion for business and helping others that began at an early age. At just 10 years old, he ran a lemonade stand to raise money for an air conditioner for his orphanage, an experience that ignited his entrepreneurial spirit and desire to make a difference. This drive carried Ken into his career, where he took his first job in construction after responding to a newspaper ad that read "no experience necessary." Despite having no prior experience, Ken eagerly embraced the role as an assistant to a builder, where he started by carrying materials and performing basic tasks. Through perseverance, a sharp mind for business, and an innate ability to learn on the job, Ken steadily advanced, eventually launching his own company. Starting CQC Home with nothing but a hammer and a vision, Ken\'s innovative approach and relentless drive have propelled the company from its humble beginnings to become a $15 million design-build powerhouse, recognized for its excellence and impact. His journey is a testament to his ability to turn visions into reality, and his strategic acumen has placed CQC Home at the forefront of the construction industry. Ken\'s exceptional sales and networking skills have been pivotal to his success, allowing him to consistently sell bigger jobs and build lasting relationships that have strengthened the business and fostered a strong sense of community. Now, with CQC Home operating as a management-run company, the Durham HBA President is channeling his passion into helping other remodelers grow and achieve success. He shares the insights and strategies that fueled his own remarkable journey, guiding others to build their own thriving businesses. Ken remains a transformative figure in the construction industry, dedicated to empowering others and continuing his legacy of excellence and impact.',
    expertise: ['Scaling', 'Management Run Company Model', 'Sales', 'Team Development', 'Organic Marketing', 'Lead Generation', 'Networking & Connections']
  },
  {
    id: 2,
    name: 'DARYL MEJEUR',
    title: 'Operations',
    bio: 'Daryl Mejeur serves as Chief Operating Officer, overseeing day-to-day operations and ensuring smooth execution of our programs.',
    avatarUrl: '/darylshot.png',
    department: 'OPERATIONS',
    background: 'Daryl Mejeur, a decorated Marine veteran, brings over 20 years of construction industry experience to his role as an operations expert. Starting his career as a laborer, Daryl\'s extensive knowledge and dedication propelled him through various management positions, ultimately leading him to specialize in operations. Daryl\'s military background, honed on the battlefields of Fallujah, Iraq in 2006, instills a unique blend of discipline, precision, and strategic foresight in his approach to operational management. His leadership and experience in managing operations across various construction firms have been pivotal in steering companies toward operational excellence. Joining CQC Home when the company was around $8M, Daryl has been instrumental in growing it to a $15M enterprise. Daryl\'s expertise in optimizing company structure, resource management, supply chain processes, and the implementation of Standard Operating Procedures (SOPs) ensures that every aspect of operations runs smoothly and efficiently. His strategic vision and meticulous attention to detail drive continuous improvement and adaptability, positioning the company for long-term growth and success.',
    expertise: ['Systems and Processes', 'Key Performance Indicators', 'ORG Chart Mapping', 'Scaling to $20M', 'Team Building', 'Culture Building', 'Job Descriptions']
  },
  {
    id: 3,
    name: 'AMANDA MITCHELL',
    title: 'Finance',
    bio: 'Amanda Mitchell, a US Army Veteran, brings firsthand entrepreneurial experience and financial expertise to her work with small business owners.',
    avatarUrl: '/amanda.png',
    department: 'FINANCE',
    background: "Amanda Mitchell, a US Army Veteran, brings a unique blend of firsthand entrepreneurial experience and financial expertise to her work with small business owners. Raised in a family of entrepreneurs, she understands the realities—the pressure, the risk, and what's truly at stake when building a business. After earning her degree in Business Administration, Amanda launched her own business, gaining a crash course in the challenges of growth and financial management. That experience led her to pursue a master's degree in accounting, where she found her passion for helping business owners understand what their numbers are actually telling them—and what to do next. Amanda has since worked closely with a variety of small businesses and has earned a reputation for turning complex financial concepts into practical, actionable guidance.",
    expertise: ['Forecasting', 'Financial Statements', 'Financial Planning', 'Taxes', 'Bookkeeping', 'Job Costing', 'Cash Flow Management']
  },
  {
    id: 4,
    name: 'IAN RIDDLE',
    title: 'Ai & Technology',
    bio: 'Ian Riddle leads our technology initiatives as Chief Technology Officer, driving innovation and digital transformation.',
    avatarUrl: '/riddleshot.png',
    department: 'TECH & AI',
    background: 'Ian grew up immersed in technology, quickly becoming proficient and fascinated by its possibilities. Influenced by his father\'s small construction company, he started working as a laborer at a young age, gaining hands-on experience and developing a keen business mindset. By age 18, he was managing 14 social media pages for various companies. Throughout college, Ian further honed his skills through estimating internships for construction companies while also using AI tools to help peers with their academics. His innovative use of artificial intelligence became a hallmark of his approach to problem-solving. After graduation, Ian moved to Chapel Hill and joined CQC Home as an estimator. Within six months, he had made significant technological advancements, including the creation of company-specific GPTs and the automation of various processes. Ian\'s continuous engagement with AI and early adoption of cutting-edge technologies, such as being one of the exclusive users to have access to Chat GPT 6.0, has positioned him at the forefront of the industry. His expertise ensures that Builders Breakthrough Advisors stays ahead in technological integration and innovation.',
    expertise: ['AI Automation', 'AI Tools & Implementation', 'CRM Optimization', 'Website Plugins', 'Tech Stack Enhancement', 'LLM\'s', 'Formulas and Parameters']
  },
  {
    id: 5,
    name: 'ANNETTE RUSHIN',
    title: 'Human Resources',
    bio: 'Annette Rushin heads our Human Resources department, focusing on talent development and organizational culture.',
    avatarUrl: '/annshot.png',
    department: 'HR',
    background: 'Annette Rushin, SHRM-SCP, serves as a human resources (HR) leader with over 25 years of experience, specializing in full lifecycle recruiting strategies and processes. As the head of HR management at Builders Breakthrough Advisory (BBA), she works closely with clients to attract, evaluate, onboard, and manage top talent, ensuring that every aspect of the staffing and HR process aligns with their mission, culture, business goals, and strategies. Annette oversees key HR functions, including compensation (benefits, training, learning, coaching, and development), HR data analytics, succession planning, employee relations, HR technology implementation (such as applicant tracking systems), performance administration, compliance, onboarding and out-processing, budgeting, and HR strategic advisory. Annette earned a bachelor\'s degree in business administration from North Carolina Wesleyan College. She holds a Senior Certified Professional in Human Resources® (SCPHR®) certification (SHRM-SCP) and is a member of the Society for Human Resource Management (SHRM).',
    expertise: ['Hiring', 'Firing', 'Onboarding', 'Employee Materials', 'Company Legal Protection', 'Company Policy', 'Job Listings']
  },
  {
    id: 6,
    name: 'PARKER SEXTON',
    title: 'Marketing',
    bio: 'Parker Sexton is our Chief Marketing Officer, leading marketing strategies and brand development initiatives.',
    avatarUrl: '/parker.png',
    department: 'MARKETING',
    background: 'Parker Sexton found his path in marketing at just sixteen, starting with one of the nation\'s top e-commerce agencies. What began as an entry-level role quickly evolved into a leadership position as he worked his way through nearly every department, gaining a full understanding of what drives growth and creativity in a competitive industry. That experience instilled not only a diverse skill set but also an appreciation for the teamwork and innovation that make great marketing happen. For Parker, success in marketing comes down to people building genuine relationships, understanding client goals, and translating ideas into strategies that feel authentic and effective. His people-first approach has become a defining part of his leadership style, fostering trust and long-term partnerships. Outside of work, Parker brings that same focus and passion to his personal life. Whether he\'s building or driving his car, playing a round of golf, or catching up with close friends, he values balance, authenticity, and making the most of every moment.',
    expertise: ['Website Creation', 'Website Enhancement', 'Google My Business', 'Digital Assets', 'Lead Generation', 'Lead Nurturing', 'Social Media', 'Paid Ads']
  }
];

const StartMeetTheTeam = ({ embedded = false }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [expandedMember, setExpandedMember] = useState(null);
  const teamMembers = BBA_TEAM_MEMBERS;

  const handleLearnMore = (memberId) => {
    const isCollapsing = expandedMember === memberId;
    setExpandedMember(isCollapsing ? null : memberId);
    // The detail panel renders above the grid, but LEARN MORE is usually tapped far
    // down the page. Without this the reader lands mid-bio on a tablet with both the
    // close control and the page's Back button off-screen.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Escape closes the panel on keyboard-equipped devices.
  useEffect(() => {
    if (!expandedMember) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setExpandedMember(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedMember]);

  const getDepartmentColor = (department) => {
    const colors = {
      SALES: 'bg-red-500 text-white',
      OPERATIONS: 'bg-blue-500 text-white',
      FINANCE: 'bg-green-500 text-white',
      'TECH & AI': 'bg-purple-500 text-white',
      HR: 'bg-yellow-500 text-white',
      MARKETING: 'bg-orange-500 text-white'
    };
    return colors[department] || 'bg-gray-500 text-white';
  };

  const bodyText = 'text-sm text-gray-600 dark:text-gray-300';
  const headingText = 'text-gray-800 dark:text-white';

  const mainContent = (
    <div className="space-y-5">
      {expandedMember && (() => {
        const expandedMemberData = teamMembers.find((m) => m.id === expandedMember);
        if (!expandedMemberData) return null;

        return (
          <div className={`${startCard} ${startCardPadding} ring-2 ring-red-500 transition-all relative`}>
            {/* Sits at the top of the panel so the bio is never a one-way trip on touch devices. */}
            <button
              type="button"
              onClick={() => setExpandedMember(null)}
              aria-label={`Close ${expandedMemberData.name} details`}
              className="absolute top-2 right-2 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" aria-hidden />
            </button>

            <div className="flex flex-col md:flex-row gap-6 pt-8 md:pt-0 md:pr-12">
              <div className="flex-shrink-0">
                {expandedMemberData.avatarUrl ? (
                  <div className="w-32 h-32 mx-auto md:mx-0 rounded-full overflow-hidden">
                    <img
                      src={expandedMemberData.avatarUrl}
                      alt={expandedMemberData.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-32 h-32 mx-auto md:mx-0 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                    <span className="text-white text-3xl font-bold">
                      {expandedMemberData.name.split(' ').map((n) => n[0]).join('')}
                    </span>
                  </div>
                )}
                <h3 className={`text-lg font-semibold text-center md:text-left mt-4 mb-2 ${headingText}`}>
                  {expandedMemberData.name}
                </h3>
                {expandedMemberData.department && (
                  <div className="flex justify-center md:justify-start mb-4">
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${getDepartmentColor(
                        expandedMemberData.department
                      )}`}
                    >
                      {expandedMemberData.department}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-1 space-y-4">
                {expandedMemberData.background && (
                  <div>
                    <h4 className={`text-sm font-semibold mb-2 ${headingText}`}>Background</h4>
                    <p className={bodyText}>{expandedMemberData.background}</p>
                  </div>
                )}

                {expandedMemberData.expertise && expandedMemberData.expertise.length > 0 && (
                  <div>
                    <h4 className={`text-sm font-semibold mb-2 ${headingText}`}>Expertise</h4>
                    <div className="flex flex-wrap gap-2">
                      {expandedMemberData.expertise.map((skill, index) => (
                        <span
                          key={index}
                          className={`px-2 py-1 text-xs rounded-md ${
                            darkMode
                              ? 'bg-red-900/30 text-red-300 border border-red-800'
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => handleLearnMore(expandedMemberData.id)}
                    className={`w-full ${startPrimaryButton}`}
                  >
                    SHOW LESS
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div
        className={`grid gap-5 ${
          expandedMember
            ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}
      >
        {teamMembers
          .filter((member) => member.id !== expandedMember)
          .map((member) => (
            <div
              key={member.id}
              className={`${startCard} ${startCardPadding} hover:shadow-md transition-shadow`}
            >
              {member.avatarUrl ? (
                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden">
                  <img src={member.avatarUrl} alt={member.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                  <span className="text-white text-3xl font-bold">
                    {member.name.split(' ').map((n) => n[0]).join('')}
                  </span>
                </div>
              )}

              <h3 className={`text-lg font-semibold text-center mb-2 ${headingText}`}>{member.name}</h3>

              {member.department && (
                <div className="flex justify-center mb-4">
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${getDepartmentColor(
                      member.department
                    )}`}
                  >
                    {member.department}
                  </span>
                </div>
              )}

              <div className="text-center">
                <button type="button" onClick={() => handleLearnMore(member.id)} className={startPrimaryButton}>
                  LEARN MORE
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <StartSubpageLayout
      embedded={embedded}
      title="Meet The Team"
      subtitle="Builders Breakthrough Advisors leadership"
      // navigate(-1) strands anyone who deep-linked or reloaded here; this page only
      // ever hangs off the Start dashboard, so send them there.
      onBack={() => navigate('/start/dashboard')}
    >
      {mainContent}
    </StartSubpageLayout>
  );
};

export default StartMeetTheTeam;
